import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import { DollarFormatter, PercentageFormatter } from '@src/components';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  METRIC_COLUMNS,
  safeNumber,
  formatShortDate,
  formatBackendFlag,
  formatWhyDriverChip,
  getStatusPillStyles,
  getRatioTone,
  getPriorityTone,
  getBuildTone,
  getConfidenceTone,
  getRecommendationTone,
  getActiveTone,
  getVerdictTone,
  getStageTone,
  getWhyDriverTone,
  getComConfidenceTone,
  getFormatTone,
  getExpectedMatureEndDate,
} from './utils';

// ─── Recommendation counts for hierarchy headers ────────────────────────────

const getRecCounts = (rows) => {
  const c = { keep: 0, watch: 0, test: 0, pause: 0 };
  rows.forEach((r) => {
    if (r.overallRecommendation === 'Keep Running') c.keep += 1;
    else if (r.overallRecommendation === 'Watch') c.watch += 1;
    else if (r.overallRecommendation === 'Test Longer') c.test += 1;
    else if (r.overallRecommendation === 'Pause') c.pause += 1;
  });
  return c;
};

const RecCountChips = ({ rows }) => {
  const c = getRecCounts(rows);
  const items = [
    { n: c.keep, label: 'Keep', bg: '#dcfce7', fg: '#166534' },
    { n: c.watch, label: 'Watch', bg: '#dbeafe', fg: '#1d4ed8' },
    { n: c.test, label: 'Test', bg: '#fef3c7', fg: '#92400e' },
    { n: c.pause, label: 'Pause', bg: '#fee2e2', fg: '#b91c1c' },
  ].filter((i) => i.n > 0);
  if (!items.length) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, ml: 1 }}>
      {items.map((i) => (
        <Box key={i.label} component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: 0.8, py: 0.15, borderRadius: 999, fontSize: 11, fontWeight: 700, backgroundColor: i.bg, color: i.fg, lineHeight: 1.4 }}>
          {i.n} {i.label}
        </Box>
      ))}
    </Box>
  );
};

// ─── Default visible columns (decision-critical only) ───────────────────────

const DEFAULT_VISIBLE_COLUMNS = new Set([
  'spend', 'leads', 'cpl', 'issues', 'cpIssue', 'demos', 'cpDemo',
  'netSold', 'effectiveCostOfMarketing', 'costOfMarketing',
  'creativeScore', 'businessScore', 'overallScore',
]);

// ─── Row background by recommendation ───────────────────────────────────────

const getRowBackground = (recommendation) => {
  if (recommendation === 'Keep Running') return 'rgba(22, 163, 74, 0.04)';
  if (recommendation === 'Pause') return 'rgba(185, 28, 28, 0.05)';
  if (recommendation === 'Watch') return 'rgba(29, 78, 216, 0.03)';
  if (recommendation === 'Dormant') return 'rgba(107, 114, 128, 0.04)';
  return undefined;
};

// ─── Trend icon ─────────────────────────────────────────────────────────────

const TrendIcon = ({ label }) => {
  if (label === 'Improving') return <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', ml: 0.5 }} />;
  if (label === 'Declining') return <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', ml: 0.5 }} />;
  return <TrendingFlatIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5 }} />;
};

// ─── Renderers ──────────────────────────────────────────────────────────────

const renderMetricValue = (value, type) => {
  if (type === 'currency') return <DollarFormatter value={value} />;
  if (type === 'ratio') return <PercentageFormatter value={value} />;
  return safeNumber(value).toLocaleString();
};

const renderStatusPill = (label, tone = 'neutral') => {
  const colors = getStatusPillStyles(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.1,
        py: 0.45,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {label || '-'}
    </Box>
  );
};

const renderDimensionValue = (column, row, onPreviewOpen, qaControls = {}, onQuickPause) => {
  if (column.key === 'previewAction') {
    const { onToggleQa, qaExpanded = false, qaLoading = false } = qaControls;
    const canPreview = row.thumbnailUrl || row.imageUrl || row.headline || row.primaryText;
    const canQa = row.clientId && row.adId;
    const showQuickPause = row.isActive && row.adId && row.clientId && row.overallRecommendation === 'Pause' && row.confidenceLabel !== 'Low';
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {canPreview ? (
          <Button size="small" variant="outlined" onClick={() => onPreviewOpen(row)} sx={{ minWidth: 64 }}>
            Preview
          </Button>
        ) : null}
        {canQa ? (
          <Button size="small" variant="text" onClick={() => onToggleQa?.(row)} sx={{ minWidth: 40 }}>
            {qaLoading ? '...' : qaExpanded ? 'Hide' : 'QA'}
          </Button>
        ) : null}
        {showQuickPause ? (
          <Tooltip title="Quick pause — opens confirmation dialog">
            <IconButton size="small" color="error" onClick={() => onPreviewOpen(row)}>
              <PauseCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    );
  }

  if (column.key === 'activeStatusLabel') return renderStatusPill(row[column.key] || '-', getActiveTone(row.isActive));
  if (column.key === 'facebookFormat') return renderStatusPill(row[column.key] || 'Unknown', getFormatTone(row[column.key]));
  if (column.key === 'stageLabel') return renderStatusPill(row[column.key] || '-', getStageTone(row[column.key]));
  if (column.key === 'confidenceLabel') return renderStatusPill(row[column.key] || '-', getConfidenceTone(row[column.key]));
  if (column.key === 'comConfidenceLabel') return renderStatusPill(row[column.key] || '-', getComConfidenceTone(row[column.key]));
  if (column.key === 'spendShareLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.spendShare));
  if (column.key === 'primaryKpiLabel') return renderStatusPill(row[column.key] || '-', 'neutral');
  if (column.key === 'predictedCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.predictedCostOfMarketing));
  if (column.key === 'effectiveCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.effectiveCostOfMarketing));
  if (column.key === 'pausePriorityLabel') return renderStatusPill(row[column.key] || '-', getPriorityTone(row.pausePriority));
  if (column.key === 'buildPriorityLabel') return renderStatusPill(row[column.key] || '-', getBuildTone(row.buildPriority));
  if (column.key === 'creativeVerdict') return renderStatusPill(row[column.key] || '-', getVerdictTone(row[column.key]));
  if (column.key === 'overallRecommendation') {
    return (
      <Stack direction="row" alignItems="center">
        {renderStatusPill(row[column.key] || '-', getRecommendationTone(row[column.key]))}
        <TrendIcon label={row.trendLabel} />
      </Stack>
    );
  }

  if (column.key === 'whyLabel') {
    const whyDriverChips = Array.isArray(row.whyDriverChips) ? row.whyDriverChips : [];
    return (
      <Stack spacing={0.6} sx={{ maxWidth: column.minWidth }}>
        <Typography noWrap sx={{ maxWidth: column.minWidth, fontSize: column.fontSize || 'inherit', lineHeight: 1.25 }}>
          {row[column.key] || '-'}
        </Typography>
        {whyDriverChips.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {whyDriverChips.map((driver) => (
              <React.Fragment key={`${driver.key}-${driver.label}`}>
                {renderStatusPill(formatWhyDriverChip(driver), getWhyDriverTone(driver.directionalDelta))}
              </React.Fragment>
            ))}
          </Box>
        ) : null}
      </Stack>
    );
  }

  if (column.key === 'adName') {
    const secondaryLabel = row.adId
      ? `Ad ID: ${row.adId}`
      : [row.themeName, row.subthemeName].filter(Boolean).join(' \u2022 ');
    return (
      <Stack spacing={0.25} sx={{ maxWidth: column.minWidth }}>
        <Typography noWrap sx={{ maxWidth: column.minWidth, fontSize: column.fontSize || 'inherit', fontWeight: 600, lineHeight: 1.25 }}>
          {row.adName || '-'}
        </Typography>
        {secondaryLabel ? (
          <Typography noWrap variant="caption" color="text.secondary" sx={{ maxWidth: column.minWidth, lineHeight: 1.2 }}>
            {secondaryLabel}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  if (column.type === 'count') return safeNumber(row[column.key]).toLocaleString();
  if (column.truncate) {
    return (
      <Typography noWrap sx={{ maxWidth: column.minWidth, fontSize: column.fontSize || 'inherit', lineHeight: 1.25 }}>
        {row[column.key] || '-'}
      </Typography>
    );
  }
  return row[column.key] || '-';
};

// ─── Recommendation summary bar ─────────────────────────────────────────────

function RecommendationSummary({ rows, viewMode }) {
  const counts = React.useMemo(() => {
    const c = { 'Keep Running': 0, Pause: 0, Watch: 0, 'Test Longer': 0, Dormant: 0 };
    rows.forEach((r) => { if (c[r.overallRecommendation] !== undefined) c[r.overallRecommendation] += 1; });
    return c;
  }, [rows]);

  const totalSpend = React.useMemo(() => rows.reduce((s, r) => s + safeNumber(r.spend), 0), [rows]);
  const pauseSpend = React.useMemo(() => rows.filter((r) => r.overallRecommendation === 'Pause').reduce((s, r) => s + safeNumber(r.spend), 0), [rows]);

  const items = [
    { label: 'Keep Running', count: counts['Keep Running'], color: '#166534', bg: '#dcfce7' },
    { label: 'Watch', count: counts.Watch, color: '#1d4ed8', bg: '#dbeafe' },
    { label: 'Test Longer', count: counts['Test Longer'], color: '#92400e', bg: '#fef3c7' },
    { label: 'Pause', count: counts.Pause, color: '#b91c1c', bg: '#fee2e2' },
    { label: 'Dormant', count: counts.Dormant, color: '#374151', bg: '#e5e7eb' },
  ];

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        {items.filter((i) => i.count > 0).map((item) => (
          <Chip
            key={item.label}
            label={`${item.count} ${item.label}`}
            size="small"
            sx={{ fontWeight: 700, fontSize: 12, backgroundColor: item.bg, color: item.color, borderRadius: 2 }}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {rows.length} {viewMode === 'ad' ? 'ads' : viewMode === 'theme' ? 'themes' : 'subthemes'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total: <DollarFormatter value={totalSpend} />
        </Typography>
        {pauseSpend > 0 ? (
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
            Pause spend: <DollarFormatter value={pauseSpend} />
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

// ─── Column visibility popover ──────────────────────────────────────────────

function ColumnVisibilityControl({ visibleColumns, onToggle, onShowAll, onShowDefault }) {
  const [anchorEl, setAnchorEl] = React.useState(null);

  return (
    <>
      <Tooltip title="Toggle column visibility">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxHeight: 420, overflowY: 'auto', minWidth: 240 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Button size="small" variant="outlined" onClick={onShowDefault}>Default</Button>
            <Button size="small" variant="outlined" onClick={onShowAll}>Show All</Button>
          </Stack>
          {METRIC_COLUMNS.map((col) => (
            <FormControlLabel
              key={col.key}
              sx={{ display: 'block', ml: 0 }}
              control={<Checkbox size="small" checked={visibleColumns.has(col.key)} onChange={() => onToggle(col.key)} />}
              label={<Typography variant="body2">{col.label}</Typography>}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
}

// ─── QA Panel ───────────────────────────────────────────────────────────────

function QaPanel({ row, qaDetail, qaLoading, qaError, startDate, endDate }) {
  const expectedMatureEndDate = getExpectedMatureEndDate(startDate, endDate);
  const expectedMatureEndDateLabel = formatShortDate(expectedMatureEndDate);
  const rowMatureEndDateLabel = formatShortDate(row.matureEndDate);
  const hasCutoffMismatch = expectedMatureEndDateLabel && rowMatureEndDateLabel && expectedMatureEndDateLabel !== rowMatureEndDateLabel;

  return (
    <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={2}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>QA Snapshot</Typography>
          <Typography variant="body2" color="text.secondary">
            Full window: {formatDateToYMD(startDate)} - {formatDateToYMD(endDate)}. Maturity cutoff: {expectedMatureEndDateLabel || 'N/A'}.
          </Typography>
          {hasCutoffMismatch ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Row maturity cutoff ({rowMatureEndDateLabel}) differs from range cutoff ({expectedMatureEndDateLabel}).
            </Alert>
          ) : null}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(220px, 280px))' }, gap: 2, alignItems: 'start' }}>
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="overline" color="text.secondary">Full Window</Typography>
            {[['Spend', row.spend, 'currency'], ['Leads', row.leads, 'count'], ['Self Books', row.selfBook, 'count'], ['Booked', row.booked, 'count'], ['Scheduled', row.scheduled, 'count'], ['Issues', row.issues, 'count'], ['Demos', row.demos, 'count'], ['Net Sold', row.netSold, 'count'], ['Net Amount', row.netAmount, 'currency']].map(([l, v, t]) => (
              <Typography key={l} variant="body2">{l}: {renderMetricValue(v, t)}</Typography>
            ))}
          </Box>
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="overline" color="text.secondary">Mature Cohorts</Typography>
            <Typography variant="body2">Through: {rowMatureEndDateLabel || expectedMatureEndDateLabel || '-'}</Typography>
            {[['Spend', row.matureSpend, 'currency'], ['Leads', row.matureLeads, 'count'], ['Issues', row.matureIssues, 'count'], ['Demos', row.matureDemos, 'count'], ['Net Sold', row.matureNetSold, 'count'], ['Net Amount', row.matureNetAmount, 'currency']].map(([l, v, t]) => (
              <Typography key={l} variant="body2">{l}: {renderMetricValue(v, t)}</Typography>
            ))}
          </Box>
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="overline" color="text.secondary">Scoring</Typography>
            {[['Stage', row.stageLabel], ['Primary KPI', row.primaryKpiLabel], ['Confidence', row.confidenceLabel], ['CoM Confidence', row.comConfidenceLabel], ['Recommendation', row.overallRecommendation], ['Format', row.facebookFormat], ['Backend', formatBackendFlag(row.hasBackendIntegration)]].map(([l, v]) => (
              <Typography key={l} variant="body2">{l}: {v || '-'}</Typography>
            ))}
          </Box>
        </Box>
        {qaError ? <Alert severity="error">{qaError}</Alert> : null}
        {qaLoading ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} /></Box>
        ) : (
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Backend Records</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Lead Created', 'Created', 'Appointment', 'Issue', 'Demo', 'Sold', 'Gross', 'Net', 'Status'].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(qaDetail?.records || []).length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center">No backend records in this window.</TableCell></TableRow>
                ) : (qaDetail?.records || []).map((r) => (
                  <TableRow key={r.id || r.unique_id}>
                    <TableCell>{formatShortDate(r.lead_created_date) || '-'}</TableCell>
                    <TableCell>{formatShortDate(r.created_date) || '-'}</TableCell>
                    <TableCell>{formatShortDate(r.appointment_date) || '-'}</TableCell>
                    <TableCell>{formatBackendFlag(r.issue)}</TableCell>
                    <TableCell>{formatBackendFlag(r.demo)}</TableCell>
                    <TableCell>{formatShortDate(r.sold_date) || '-'}</TableCell>
                    <TableCell>{renderMetricValue(r.gross_amount, 'currency')}</TableCell>
                    <TableCell>{renderMetricValue(r.net_amount, 'currency')}</TableCell>
                    <TableCell>{r.status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {qaDetail?.totalCount > (qaDetail?.records || []).length ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Showing {(qaDetail?.records || []).length} of {qaDetail?.totalCount}.
              </Typography>
            ) : null}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Data row ───────────────────────────────────────────────────────────────

const DataRow = React.memo(function DataRow({
  row, dimensionColumns, visibleMetricColumns, onPreviewOpen, onToggleQa,
  expandedQaRows, qaRowLoading, qaRowDetails, qaRowErrors, startDate, endDate,
}) {
  const qaExpanded = Boolean(expandedQaRows[row.uuid]);
  const colSpan = dimensionColumns.length + visibleMetricColumns.length;
  const rowBg = getRowBackground(row.overallRecommendation);

  return (
    <React.Fragment>
      <TableRow hover sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 48px', backgroundColor: rowBg }}>
        {dimensionColumns.map((column) => (
          <TableCell
            key={`${row.uuid}-${column.key}`}
            title={column.truncate ? (row[column.key] || '') : undefined}
            sx={column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 2, backgroundColor: rowBg || 'background.paper' } : undefined}
          >
            {renderDimensionValue(column, row, onPreviewOpen, {
              onToggleQa,
              qaExpanded,
              qaLoading: Boolean(qaRowLoading[row.uuid]),
            }, onPreviewOpen)}
          </TableCell>
        ))}
        {visibleMetricColumns.map((column) => (
          <TableCell key={column.key}>{renderMetricValue(row[column.key], column.type)}</TableCell>
        ))}
      </TableRow>
      {qaExpanded ? (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ p: 0, bgcolor: '#fcfcfd' }}>
            <QaPanel row={row} qaDetail={qaRowDetails[row.uuid]} qaLoading={Boolean(qaRowLoading[row.uuid])} qaError={qaRowErrors[row.uuid]} startDate={startDate} endDate={endDate} />
          </TableCell>
        </TableRow>
      ) : null}
    </React.Fragment>
  );
});

// ─── Main table component ───────────────────────────────────────────────────

export default function CreativeTable({
  viewMode,
  decisionRows,
  adHierarchy,
  dimensionColumns,
  totals,
  loading,
  loadingClientMeta,
  startDate,
  endDate,
  onPreviewOpen,
  onToggleQa,
  expandedClients,
  expandedCampaigns,
  expandedAdSets,
  expandedQaRows,
  qaRowDetails,
  qaRowLoading,
  qaRowErrors,
  toggleClientExpanded,
  toggleCampaignExpanded,
  toggleAdSetExpanded,
}) {
  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState(() => new Set(DEFAULT_VISIBLE_COLUMNS));
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDirection, setSortDirection] = React.useState('desc');
  const [focusMode, setFocusMode] = React.useState('all');
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleToggleColumn = React.useCallback((key) => {
    setVisibleColumnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleShowAll = React.useCallback(() => {
    setVisibleColumnKeys(new Set(METRIC_COLUMNS.map((c) => c.key)));
  }, []);

  const handleShowDefault = React.useCallback(() => {
    setVisibleColumnKeys(new Set(DEFAULT_VISIBLE_COLUMNS));
  }, []);

  const visibleMetricColumns = React.useMemo(
    () => METRIC_COLUMNS.filter((col) => visibleColumnKeys.has(col.key)),
    [visibleColumnKeys]
  );

  const handleSort = React.useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setSortDirection('desc');
      return key;
    });
  }, []);

  const matchesSearch = React.useCallback((row) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (row.adName || '').toLowerCase().includes(q)
      || (row.clientName || '').toLowerCase().includes(q)
      || (row.themeName || '').toLowerCase().includes(q)
      || (row.subthemeName || '').toLowerCase().includes(q)
      || (row.campaign || '').toLowerCase().includes(q)
      || (row.adSetName || '').toLowerCase().includes(q)
      || (row.adId || '').toLowerCase().includes(q)
      || (row.verticalName || '').toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const focusedRows = React.useMemo(() => {
    let rows = decisionRows;
    if (focusMode === 'actionable') rows = rows.filter((r) => r.overallRecommendation === 'Keep Running' || r.overallRecommendation === 'Pause');
    else if (focusMode === 'pause') rows = rows.filter((r) => r.overallRecommendation === 'Pause');
    if (searchQuery) rows = rows.filter(matchesSearch);
    return rows;
  }, [decisionRows, focusMode, matchesSearch, searchQuery]);

  // Sort (for theme/subtheme flat views)
  const sortedRows = React.useMemo(() => {
    if (!sortKey || viewMode === 'ad') return focusedRows;
    const col = METRIC_COLUMNS.find((c) => c.key === sortKey) || dimensionColumns.find((c) => c.key === sortKey);
    if (!col) return focusedRows;
    return [...focusedRows].sort((a, b) => {
      const av = safeNumber(a[sortKey]);
      const bv = safeNumber(b[sortKey]);
      return sortDirection === 'asc' ? av - bv : bv - av;
    });
  }, [focusedRows, dimensionColumns, sortDirection, sortKey, viewMode]);

  const focusedHierarchy = React.useMemo(() => {
    if (viewMode !== 'ad' && !searchQuery) return adHierarchy;
    const needsFocusFilter = focusMode !== 'all';
    const needsSearch = Boolean(searchQuery);
    if (!needsFocusFilter && !needsSearch) return adHierarchy;

    const focusFilter = focusMode === 'pause'
      ? (r) => r.overallRecommendation === 'Pause'
      : focusMode === 'actionable'
        ? (r) => r.overallRecommendation === 'Keep Running' || r.overallRecommendation === 'Pause'
        : () => true;

    return adHierarchy
      .map((cg) => ({
        ...cg,
        campaigns: cg.campaigns
          .map((camp) => ({
            ...camp,
            adSets: camp.adSets
              .map((as) => ({ ...as, rows: as.rows.filter((r) => focusFilter(r) && matchesSearch(r)) }))
              .filter((as) => as.rows.length > 0),
          }))
          .filter((camp) => camp.adSets.length > 0),
      }))
      .filter((cg) => cg.campaigns.length > 0);
  }, [adHierarchy, focusMode, matchesSearch, searchQuery, viewMode]);

  const colSpan = dimensionColumns.length + visibleMetricColumns.length;

  if (loading || loadingClientMeta) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      {/* 3. Recommendation summary bar */}
      <RecommendationSummary rows={decisionRows} viewMode={viewMode} />

      {/* Toolbar: search + focus mode + column controls */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search ads, clients, themes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            value={focusMode}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) setFocusMode(v); }}
            sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.4, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: 12 } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="actionable">Actionable Only</ToggleButton>
            <ToggleButton value="pause">Pause Only</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <ColumnVisibilityControl visibleColumns={visibleColumnKeys} onToggle={handleToggleColumn} onShowAll={handleShowAll} onShowDefault={handleShowDefault} />
      </Stack>

      <TableContainer sx={{ maxHeight: '74vh' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1800 }}>
          <TableHead>
            <TableRow>
              {dimensionColumns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    minWidth: column.minWidth,
                    ...(column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 3, backgroundColor: 'background.paper' } : {}),
                  }}
                >
                  {column.helpText ? (
                    <Tooltip title={column.helpText} arrow>
                      <Box component="span" sx={{ cursor: 'help' }}>{column.label}</Box>
                    </Tooltip>
                  ) : column.label}
                </TableCell>
              ))}
              {visibleMetricColumns.map((column) => (
                <TableCell key={column.key} sx={{ minWidth: 110 }} sortDirection={sortKey === column.key ? sortDirection : false}>
                  <TableSortLabel
                    active={sortKey === column.key}
                    direction={sortKey === column.key ? sortDirection : 'desc'}
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(viewMode === 'ad' ? focusedHierarchy.length === 0 : sortedRows.length === 0) ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                  {focusMode !== 'all' ? 'No ads match the current focus filter.' : 'No rows match the selected filters.'}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {viewMode === 'ad' ? focusedHierarchy.map((clientGroup) => {
                  const clientExpanded = expandedClients[clientGroup.key] ?? true;
                  return (
                    <React.Fragment key={clientGroup.key}>
                      <TableRow sx={{ contentVisibility: 'auto' }}>
                        <TableCell colSpan={colSpan} sx={{ bgcolor: '#f6f8fb', borderBottomColor: 'divider', py: 1.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" onClick={() => toggleClientExpanded(clientGroup.key)}>
                              {clientExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                            <Typography sx={{ fontWeight: 800 }}>{clientGroup.clientName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {clientGroup.verticalName || 'Unassigned'} &bull; {clientGroup.rows.length} ads &bull; <DollarFormatter value={clientGroup.spend} />
                            </Typography>
                            <RecCountChips rows={clientGroup.rows} />
                          </Box>
                        </TableCell>
                      </TableRow>
                      {clientExpanded ? clientGroup.campaigns.map((campaignGroup) => {
                        const campaignExpanded = expandedCampaigns[campaignGroup.key] ?? true;
                        return (
                          <React.Fragment key={campaignGroup.key}>
                            <TableRow sx={{ contentVisibility: 'auto' }}>
                              <TableCell colSpan={colSpan} sx={{ bgcolor: '#fbfcfe', borderBottomColor: 'divider', py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3 }}>
                                  <IconButton size="small" onClick={() => toggleCampaignExpanded(campaignGroup.key)}>
                                    {campaignExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                  </IconButton>
                                  <Typography sx={{ fontWeight: 700 }}>{campaignGroup.campaign}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {campaignGroup.rows.length} ads &bull; <DollarFormatter value={campaignGroup.spend} />
                                  </Typography>
                                  <RecCountChips rows={campaignGroup.rows} />
                                </Box>
                              </TableCell>
                            </TableRow>
                            {campaignExpanded ? campaignGroup.adSets.map((adSetGroup) => {
                              const adSetExpanded = expandedAdSets[adSetGroup.key] ?? true;
                              return (
                                <React.Fragment key={adSetGroup.key}>
                                  <TableRow sx={{ contentVisibility: 'auto' }}>
                                    <TableCell colSpan={colSpan} sx={{ bgcolor: '#ffffff', borderBottomColor: 'divider', py: 0.9 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 6 }}>
                                        <IconButton size="small" onClick={() => toggleAdSetExpanded(adSetGroup.key)}>
                                          {adSetExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                        </IconButton>
                                        <Typography sx={{ fontWeight: 700 }}>{adSetGroup.adSetName}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {adSetGroup.rows.length} ads &bull; <DollarFormatter value={adSetGroup.spend} />
                                        </Typography>
                                        <RecCountChips rows={adSetGroup.rows} />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  {adSetExpanded ? adSetGroup.rows.map((row) => (
                                    <DataRow
                                      key={row.uuid}
                                      row={row}
                                      dimensionColumns={dimensionColumns}
                                      visibleMetricColumns={visibleMetricColumns}
                                      onPreviewOpen={onPreviewOpen}
                                      onToggleQa={onToggleQa}
                                      expandedQaRows={expandedQaRows}
                                      qaRowLoading={qaRowLoading}
                                      qaRowDetails={qaRowDetails}
                                      qaRowErrors={qaRowErrors}
                                      startDate={startDate}
                                      endDate={endDate}
                                    />
                                  )) : null}
                                </React.Fragment>
                              );
                            }) : null}
                          </React.Fragment>
                        );
                      }) : null}
                    </React.Fragment>
                  );
                }) : sortedRows.map((row) => (
                  <TableRow key={row.uuid} hover sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 48px', backgroundColor: getRowBackground(row.overallRecommendation) }}>
                    {dimensionColumns.map((column) => (
                      <TableCell
                        key={`${row.uuid}-${column.key}`}
                        title={column.truncate ? (row[column.key] || '') : undefined}
                        sx={column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 2, backgroundColor: getRowBackground(row.overallRecommendation) || 'background.paper' } : undefined}
                      >
                        {renderDimensionValue(column, row, onPreviewOpen)}
                      </TableCell>
                    ))}
                    {visibleMetricColumns.map((column) => (
                      <TableCell key={column.key}>{renderMetricValue(row[column.key], column.type)}</TableCell>
                    ))}
                  </TableRow>
                ))}

                <TableRow>
                  {dimensionColumns.map((column, index) => (
                    <TableCell key={`total-${column.key}`} sx={{ fontWeight: 700, ...(column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 2, backgroundColor: 'background.paper' } : {}) }}>
                      {index === 0 ? 'Total' : '-'}
                    </TableCell>
                  ))}
                  {visibleMetricColumns.map((column) => (
                    <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                      {renderMetricValue(totals[column.key], column.type)}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
